import { createAsyncThunk } from "@reduxjs/toolkit"
import { flushSync } from "react-dom"
import { store } from "../../../store"
import { 
  uploadCaseFilesByFileNames,
  deleteCaseFiles,
  fetchZipsFromCacheOrRemote,
} from "../clinicalThunkApi"
import {
  updateCaseById,
} from "@/core/app/slices/case/caseThunkApi"
import {
  setRefinementReady
} from "../clinicalSlice"
import {
  setOpenInitialFinal,
  setOpenGrid,
  setIsCreateNewRefinement,
  setIsCreateSelfplanRefinement,
  setStageShowType,
} from "@/UDTreat/udTreatSlice"

import {
  getRefinementType,
} from "../clinical.types"
import {
  downloadRefinementFile
} from "../clinical.service"

import {
  caseManagement,
  treatView,
  refinement,
  viewControlInTreatment,
  resourcesSynchronization,
} from "@/gluelayer"


export const fetchRefinementJsonFile = createAsyncThunk(
  "clinicalService/fetchRefinementJsonFile",
  async (
    { patientId, caseId, refinementFiles }: getRefinementType,
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as any).userService.user.current_orgId
    try {
      return await downloadRefinementFile({ orgId, patientId, caseId, refinementFiles})
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)

export const fetchRefinementZipFile = createAsyncThunk(
  "clinicalService/fetchRefinementZipFile",
  async (
    { patientId, caseId, refinementFiles }: getRefinementType,
    { rejectWithValue, getState },
  ) => {
    const orgId = (getState() as any).userService.user.current_orgId
    try {
      return await downloadRefinementFile({ orgId, patientId, caseId, refinementFiles})
    } catch (err) {
      return rejectWithValue(err)
    }
  },
)


// Open creating refinement workflow
export const openRefinement = createAsyncThunk(
  "refinement/openRefinement",
  async (
    { isSelfplan=false } : {isSelfplan:boolean},
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      viewControlInTreatment.setViewType("front")
      const dispatch = store.dispatch
      await dispatch(setOpenInitialFinal(false))
      flushSync(() => setOpenGrid(false))
      treatView.startSplitScreenToCreateRefinement()
      dispatch(setIsCreateNewRefinement(true))
      if (isSelfplan) {
        dispatch(setIsCreateSelfplanRefinement(true))
      }
      dispatch(setStageShowType("compare"))
    } catch (err) {
      console.warn("Something wrong in conversion case ...,", err);
      return rejectWithValue(err)
    }
  }
)

/**
 * Fetch last refinement data from server.
 */
export const fetchLastRefinementData = createAsyncThunk(
  "refinement/fetchLastRefinementData",
  async (
    { caseId, patientId }: any,
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const dispatch = store.dispatch
      const folderName = refinement.getLastRefinementName()

      let refinementFiles = []
      let refinementZipFolderNames = []
      const isExisted = caseManagement.IsRefinementItemExisted(folderName)
      if (isExisted == false) {
        refinementZipFolderNames.push(folderName)
        refinementFiles.push(folderName + ".zip")
        const r = await dispatch(
          fetchRefinementZipFile({
            orgId: "",
            patientId,
            caseId,
            refinementFiles,
          }),
        )
        if (r.type.endsWith("rejected")) {
          console.warn("Failed to fetch refinement zip");
          return rejectWithValue(r.payload);
        }
        const { payload } = r
        let refinementZips: object = []
        for (let i = 0; i < (payload as []).length; i++) {
          refinementZips["RetouchHistoryZip" + i] = payload[i].data
        }
        await caseManagement
          .AddARefinementHistory(
            refinementZipFolderNames,
            refinementZips as Record<string, Blob>,
          )
      }
    } catch (err) {
      console.warn("Something wrong in fetch refinement data ...,", err);
      return rejectWithValue(err)
    }
  }
)

/**
 * undoRefinement
 */
export const undoRefinement = createAsyncThunk(
  "refinement/undoRefinement",
  async (
    { caseId, patientId,lockFunc,needOpenRefinement = true  }: any,
    { rejectWithValue, getState, dispatch },
  ) => {
    try {
      const dispatch = store.dispatch
      
      //console.log('undo refinement finished00000',(getState() as any).udTreatService.refinementNum);

      dispatch(setRefinementReady(false))
      // open refinement dialog
      const fetchResult = await dispatch(
        fetchLastRefinementData({
          caseId,
          patientId,
        }),
      )
      if (fetchResult.type.endsWith("rejected")) {
        console.warn("Failed to fetch last refinement data, terminating undoRefinement");
        return rejectWithValue(fetchResult.payload);
      }
      const ok1 = await ensureFolder(caseId, patientId, "Setting Data0", "bulk0")
      const ok2 = await ensureFolder(caseId, patientId, "Raw", "raw")
      if (!ok1 || !ok2) {
        console.warn("Failed to ensure folders");
        return rejectWithValue("Failed to ensure folders");
      }
      refinement.undoRefinement();

      // update refinement-ready status to notify update the refinementNum.
      await dispatch(setRefinementReady(true))

      // await new Promise((resolve)=>lockFunc(async ()=>{
      //   await dispatch(uploadCaseFilesByFileNames({
      //     patientId,
      //     caseId,
      //     fileNames: ["bulk0.zip","bulk1.zip","bulk2.zip","bulk10.zip","raw.zip","photo.zip","RetouchHistory.json"],
      //   }))

      //   await dispatch(openRefinement({
      //     isSelfplan: true,
      //   }))
      //   resolve(true)
      // }))
      await dispatch(uploadCaseFilesByFileNames({
        patientId,
        caseId,
        fileNames: ["bulk0.zip","bulk1.zip","bulk2.zip","bulk10.zip","raw.zip","photo.zip","RetouchHistory.json"],
      }))

      if (needOpenRefinement) {
        await dispatch(openRefinement({
          isSelfplan: true,
        }))
      }

      // clear photos
      // console.log('clearing previous refinement file type.json');
      const { refinementNum, stageObjBySlice } = (getState() as any).udTreatService
      //await dispatch(deleteCaseFiles({patientId,caseId,fileNames:[`type${refinementNum}.json`]}))

      const updatePayload: Record<string, any> = {
        course_check_number: refinementNum - 1,
        planned_upper: stageObjBySlice.upList.length - 1 > 0 ? stageObjBySlice.upList.length - 1 : 0,
        planned_lower: stageObjBySlice.downList.length - 1 > 0 ? stageObjBySlice.downList.length - 1 : 0,
      }

      const originArchTypeData = resourcesSynchronization.getFileDirectly(
        "ArchType.json",
        "Setting Data2",
        "utf8",
      )
      if (originArchTypeData) {
        try {
          const dataObject = JSON.parse(originArchTypeData)
          let upArchType = "--"
          let downArchType = "--"
          if (dataObject.hasOwnProperty("upArch")) {
            upArchType = dataObject.upArch === 0 ? "A" : "R"
          }
          if (dataObject.hasOwnProperty("downArch")) {
            downArchType = dataObject.downArch === 0 ? "A" : "R"
          }
          const { caseDetail } = (getState() as any).caseService
          if (caseDetail?.udesign_json) {
            const udesign_json = JSON.parse(caseDetail.udesign_json)
            const newUdesignJson = { ...udesign_json }
            newUdesignJson.UpperType = upArchType
            newUdesignJson.LowerType = downArchType
            updatePayload.udesign_category = `${upArchType}/${downArchType}`
            updatePayload.custom_category = `${upArchType}/${downArchType}`
            updatePayload.case_extra_attrs = JSON.stringify(newUdesignJson)
          }
        } catch (archTypeErr) {
          console.warn("Failed to parse ArchType.json or caseDetail.udesign_json, skip case_extra_attrs update", archTypeErr)
        }
      }

      // update refinement course check number
      await dispatch(
        updateCaseById({
          patientId,
          caseId,
          payload: updatePayload,
        }),
      )


    } catch (err) {
      console.warn("Something wrong in undo refinement ...,", err);
      return rejectWithValue(err)
    }
  }
)

const downloadZipWithRetry = async (
  caseId, 
  patientId,
  zipName: string,
  maxRetries = 3,
  delay = 1000,
): Promise<boolean> => {
  const fullZipName = `${zipName}.zip`
  const dispatch = store.dispatch

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const ret: any = await dispatch(
        fetchZipsFromCacheOrRemote({
          caseId,
          patientId,
          zipNames: [zipName],
          isDownloadRetouchHistory: false,
        }),
      ).unwrap()

      if (fullZipName in ret) {
        await resourcesSynchronization.writeCaseFilesToVirtualFS({
          [fullZipName]: ret[fullZipName],
        })
        return true
      }

      console.warn(`${fullZipName} not found, retry ${attempt}`)
    } catch (err) {
      console.error(`Download ${zipName} error:`, err)
    }

    if (attempt < maxRetries) {
      await new Promise((r) => setTimeout(r, delay))
    }
  }

  return false
}

const ensureFolder = async (
  caseId, 
  patientId,
  folderName: string,
  zipName: string,
): Promise<boolean> => {
  const getFiles = async () =>
    await resourcesSynchronization.getFileListFromVirtualFS(folderName)

  const files = await getFiles()

  if (Object.keys(files).length > 0) {
    return true
  }

  const success = await downloadZipWithRetry(caseId, patientId, zipName)

  if (!success) {
    console.error(`Failed to download ${zipName}.zip`)
    return false
  }

  const updatedFiles = await getFiles()

  if (Object.keys(updatedFiles).length === 0) {
    console.error(`${folderName} still empty after download`)
    return false
  }

  return true
}